'use client';
import React, { useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SyncRecord {
  id: string;
  type: 'proposal' | 'customer';
  name: string;
  zohoId?: string;
  action: 'insert' | 'update';
  status: 'success' | 'error';
  message: string;
  timestamp: string;
}

interface SyncStats {
  totalSynced: number;
  proposals: number;
  customers: number;
  lastSync: string | null;
  errors: number;
}

interface ZohoSyncDashboardProps {
  isLight: boolean;
}

const MOCK_HISTORY: SyncRecord[] = [
  { id: 's1', type: 'proposal', name: 'PL-2024-042 · ADNOC Network Upgrade', zohoId: '4150868000001234567', action: 'insert', status: 'success', message: 'Deal created in Zoho CRM (Stage: Proposal/Price Quote, Amount: 142,000 OMR)', timestamp: '2026-06-24T08:30:00Z' },
  { id: 's2', type: 'customer', name: 'Etisalat Group', zohoId: '4150868000001234568', action: 'update', status: 'success', message: 'Account updated in Zoho CRM', timestamp: '2026-06-24T07:15:00Z' },
  { id: 's3', type: 'proposal', name: 'PL-2024-039 · Dubai Municipality Cloud', zohoId: '4150868000001234569', action: 'update', status: 'success', message: 'Deal updated in Zoho CRM (Stage: Closed Won, Amount: 215,000 OMR)', timestamp: '2026-06-23T16:45:00Z' },
  { id: 's4', type: 'customer', name: 'Oman LNG', zohoId: undefined, action: 'insert', status: 'error', message: 'Zoho CRM credentials are not configured. Please set ZOHO_CLIENT_ID, ZOHO_CLIENT_SECRET, and ZOHO_REFRESH_TOKEN.', timestamp: '2026-06-23T14:20:00Z' },
  { id: 's5', type: 'proposal', name: 'PL-2024-037 · CPL Security Systems', zohoId: '4150868000001234570', action: 'insert', status: 'success', message: 'Deal created in Zoho CRM (Stage: Qualification, Amount: 15,000 OMR)', timestamp: '2026-06-22T11:00:00Z' },
];

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date('2026-06-24T09:56:03Z');
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export default function ZohoSyncDashboard({ isLight }: ZohoSyncDashboardProps) {
  const [history, setHistory] = useState<SyncRecord[]>(MOCK_HISTORY);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'proposal' | 'customer' | 'error'>('all');
  const [expandedError, setExpandedError] = useState<string | null>(null);

  const cardBg = isLight ? '#FFFFFF' : '#0B1628';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(30,144,255,0.12)';
  const mutedText = isLight ? '#64748B' : '#6B7FA3';
  const headingText = isLight ? '#0F172A' : '#FFFFFF';
  const sectionBg = isLight ? '#F8FAFC' : '#0D1829';

  const stats: SyncStats = {
    totalSynced: history.filter(r => r.status === 'success').length,
    proposals: history.filter(r => r.type === 'proposal' && r.status === 'success').length,
    customers: history.filter(r => r.type === 'customer' && r.status === 'success').length,
    lastSync: history.length > 0 ? history[0].timestamp : null,
    errors: history.filter(r => r.status === 'error').length,
  };

  const filteredHistory = history.filter(r => {
    if (filterType === 'all') return true;
    if (filterType === 'error') return r.status === 'error';
    return r.type === filterType;
  });

  const handleManualSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      // Attempt a lightweight verify to confirm credentials work
      const res = await fetch('/api/zoho/verify-credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();

      if (data.valid) {
        const newRecord: SyncRecord = {
          id: `s${Date.now()}`,
          type: 'proposal',
          name: 'Manual Sync Check',
          zohoId: undefined,
          action: 'update',
          status: 'success',
          message: 'Connection verified. All pending records are up to date.',
          timestamp: new Date().toISOString(),
        };
        setHistory(prev => [newRecord, ...prev]);
        setSyncResult({ status: 'success', message: 'Sync complete — Zoho CRM is up to date.' });
      } else {
        const newRecord: SyncRecord = {
          id: `s${Date.now()}`,
          type: 'proposal',
          name: 'Manual Sync Check',
          zohoId: undefined,
          action: 'insert',
          status: 'error',
          message: data.error || 'Credentials verification failed.',
          timestamp: new Date().toISOString(),
        };
        setHistory(prev => [newRecord, ...prev]);
        setSyncResult({ status: 'error', message: data.error || 'Sync failed — check credentials.' });
      }
    } catch (err: any) {
      const newRecord: SyncRecord = {
        id: `s${Date.now()}`,
        type: 'proposal',
        name: 'Manual Sync Check',
        zohoId: undefined,
        action: 'insert',
        status: 'error',
        message: err.message || 'Network error during sync.',
        timestamp: new Date().toISOString(),
      };
      setHistory(prev => [newRecord, ...prev]);
      setSyncResult({ status: 'error', message: err.message || 'Network error.' });
    } finally {
      setSyncing(false);
    }
  }, []);

  const clearErrors = () => {
    setHistory(prev => prev.filter(r => r.status !== 'error'));
    setSyncResult(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-800 text-base"
              style={{ background: 'linear-gradient(135deg, #E42527, #F5A623)', color: '#fff' }}
            >
              Z
            </div>
            <div>
              <h3 className="text-sm font-700" style={{ color: headingText }}>Zoho Sync Dashboard</h3>
              <p className="text-xs mt-0.5" style={{ color: mutedText }}>
                {stats.lastSync
                  ? `Last synced ${formatRelativeTime(stats.lastSync)}`
                  : 'No syncs recorded yet'}
              </p>
            </div>
          </div>
          <button
            onClick={handleManualSync}
            disabled={syncing}
            className="flex items-center gap-2 text-xs font-700 px-4 py-2.5 rounded-xl transition-all disabled:opacity-60 shrink-0"
            style={{
              background: 'linear-gradient(135deg, #E42527, #F5A623)',
              color: '#fff',
              boxShadow: '0 2px 10px rgba(228,37,39,0.25)',
            }}
          >
            <Icon
              name={syncing ? 'ArrowPathIcon' : 'CloudArrowUpIcon'}
              size={13}
              className={syncing ? 'animate-spin' : ''}
            />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </button>
        </div>

        {/* Sync result banner */}
        {syncResult && (
          <div
            className="flex items-center gap-2.5 mt-4 rounded-lg px-3 py-2.5"
            style={{
              background: syncResult.status === 'success' ? 'rgba(0,214,143,0.08)' : 'rgba(255,71,87,0.08)',
              border: `1px solid ${syncResult.status === 'success' ? 'rgba(0,214,143,0.25)' : 'rgba(255,71,87,0.25)'}`,
            }}
          >
            <Icon
              name={syncResult.status === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'}
              size={14}
              style={{ color: syncResult.status === 'success' ? '#00D68F' : '#FF4757', flexShrink: 0 } as React.CSSProperties}
            />
            <p className="text-xs font-600 flex-1" style={{ color: syncResult.status === 'success' ? '#00D68F' : '#FF4757' }}>
              {syncResult.message}
            </p>
            <button onClick={() => setSyncResult(null)} style={{ color: mutedText }}>
              <Icon name="XMarkIcon" size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Synced', value: stats.totalSynced, icon: 'CloudArrowUpIcon', color: '#1E90FF' },
          { label: 'Deals Synced', value: stats.proposals, icon: 'DocumentTextIcon', color: '#00D68F' },
          { label: 'Accounts Synced', value: stats.customers, icon: 'BuildingOfficeIcon', color: '#F5A623' },
          { label: 'Sync Errors', value: stats.errors, icon: 'ExclamationCircleIcon', color: stats.errors > 0 ? '#FF4757' : mutedText },
        ].map(({ label, value, icon, color }) => (
          <div
            key={label}
            className="rounded-xl p-4"
            style={{ background: cardBg, border: `1px solid ${borderColor}` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                <Icon name={icon as any} size={13} style={{ color } as React.CSSProperties} />
              </div>
              <span className="text-[10px] font-600" style={{ color: mutedText }}>{label}</span>
            </div>
            <p className="text-xl font-800" style={{ color: value > 0 && label === 'Sync Errors' ? '#FF4757' : headingText }}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Sync History */}
      <div className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: `1px solid ${borderColor}`, background: sectionBg }}>
          <p className="text-xs font-700" style={{ color: headingText }}>Sync History</p>
          <div className="flex items-center gap-2">
            {/* Filter pills */}
            <div className="flex items-center gap-1">
              {(['all', 'proposal', 'customer', 'error'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilterType(f)}
                  className="text-[10px] font-700 px-2.5 py-1 rounded-full transition-all capitalize"
                  style={{
                    background: filterType === f
                      ? f === 'error' ? 'rgba(255,71,87,0.15)' : 'rgba(30,144,255,0.15)'
                      : 'transparent',
                    color: filterType === f
                      ? f === 'error' ? '#FF4757' : '#1E90FF'
                      : mutedText,
                    border: `1px solid ${filterType === f
                      ? f === 'error' ? 'rgba(255,71,87,0.3)' : 'rgba(30,144,255,0.3)'
                      : 'transparent'}`,
                  }}
                >
                  {f === 'all' ? 'All' : f === 'proposal' ? 'Deals' : f === 'customer' ? 'Accounts' : 'Errors'}
                </button>
              ))}
            </div>
            {stats.errors > 0 && (
              <button
                onClick={clearErrors}
                className="text-[10px] font-600 px-2.5 py-1 rounded-full transition-all"
                style={{ background: 'rgba(255,71,87,0.08)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)' }}
              >
                Clear Errors
              </button>
            )}
          </div>
        </div>

        {/* History list */}
        {filteredHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Icon name="CloudArrowUpIcon" size={28} style={{ color: mutedText } as React.CSSProperties} />
            <p className="text-sm font-600" style={{ color: mutedText }}>No sync records found</p>
            <p className="text-xs" style={{ color: mutedText }}>Sync a proposal or customer to see history here</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor }}>
            {filteredHistory.map(record => (
              <div key={record.id} className="px-5 py-3.5">
                <div className="flex items-start gap-3">
                  {/* Type icon */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{
                      background: record.status === 'error' ?'rgba(255,71,87,0.1)'
                        : record.type === 'proposal' ?'rgba(0,214,143,0.1)' :'rgba(245,166,35,0.1)',
                    }}
                  >
                    <Icon
                      name={record.status === 'error' ? 'ExclamationCircleIcon' : record.type === 'proposal' ? 'DocumentTextIcon' : 'BuildingOfficeIcon'}
                      size={14}
                      style={{
                        color: record.status === 'error' ? '#FF4757' : record.type === 'proposal' ? '#00D68F' : '#F5A623',
                      } as React.CSSProperties}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-700 truncate" style={{ color: headingText }}>
                        {record.name}
                      </span>
                      <span
                        className="text-[9px] font-700 px-1.5 py-0.5 rounded-full shrink-0"
                        style={{
                          background: record.type === 'proposal' ? 'rgba(0,214,143,0.1)' : 'rgba(245,166,35,0.1)',
                          color: record.type === 'proposal' ? '#00D68F' : '#F5A623',
                        }}
                      >
                        {record.type === 'proposal' ? 'Deal' : 'Account'}
                      </span>
                      <span
                        className="text-[9px] font-700 px-1.5 py-0.5 rounded-full shrink-0"
                        style={{
                          background: record.action === 'insert' ? 'rgba(30,144,255,0.1)' : 'rgba(107,127,163,0.1)',
                          color: record.action === 'insert' ? '#1E90FF' : mutedText,
                        }}
                      >
                        {record.action === 'insert' ? 'Created' : 'Updated'}
                      </span>
                    </div>

                    {/* Success message or error toggle */}
                    {record.status === 'success' ? (
                      <p className="text-[11px] mt-0.5" style={{ color: mutedText }}>{record.message}</p>
                    ) : (
                      <div>
                        <button
                          onClick={() => setExpandedError(expandedError === record.id ? null : record.id)}
                          className="flex items-center gap-1 text-[11px] mt-0.5 transition-all"
                          style={{ color: '#FF4757' }}
                        >
                          <Icon name="ExclamationTriangleIcon" size={11} />
                          Sync failed — click to view error
                          <Icon name={expandedError === record.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={10} />
                        </button>
                        {expandedError === record.id && (
                          <div
                            className="mt-2 rounded-lg px-3 py-2.5 text-[11px] font-500"
                            style={{ background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', color: '#FF4757' }}
                          >
                            {record.message}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Zoho ID */}
                    {record.zohoId && (
                      <p className="text-[10px] mt-1 font-500" style={{ color: mutedText }}>
                        Zoho ID: <span className="font-600" style={{ color: '#1E90FF' }}>{record.zohoId}</span>
                      </p>
                    )}
                  </div>

                  {/* Timestamp + status */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span
                      className="text-[9px] font-700 px-2 py-0.5 rounded-full"
                      style={{
                        background: record.status === 'success' ? 'rgba(0,214,143,0.1)' : 'rgba(255,71,87,0.1)',
                        color: record.status === 'success' ? '#00D68F' : '#FF4757',
                        border: `1px solid ${record.status === 'success' ? 'rgba(0,214,143,0.25)' : 'rgba(255,71,87,0.25)'}`,
                      }}
                    >
                      {record.status === 'success' ? '✓ Synced' : '✗ Failed'}
                    </span>
                    <span className="text-[10px]" style={{ color: mutedText }}>
                      {formatRelativeTime(record.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="rounded-xl p-4" style={{ background: isLight ? '#F0F9FF' : 'rgba(30,144,255,0.04)', border: '1px solid rgba(30,144,255,0.15)' }}>
        <div className="flex items-start gap-2.5">
          <Icon name="InformationCircleIcon" size={14} style={{ color: '#1E90FF', marginTop: 1, flexShrink: 0 } as React.CSSProperties} />
          <div className="space-y-1">
            <p className="text-xs font-700" style={{ color: '#1E90FF' }}>How Sync Works</p>
            <p className="text-[11px]" style={{ color: mutedText }}>
              Proposals are pushed as <strong style={{ color: headingText }}>Deals</strong> and customers as <strong style={{ color: headingText }}>Accounts + Contacts</strong> in Zoho CRM. Use the sync button on individual proposals or customers to push them, or click <strong style={{ color: headingText }}>Sync Now</strong> above to verify the connection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
